<template>
  <div class="stats-table" role="table" aria-label="Höchst-, Tiefst- und Durchschnittswert je Saison">
    <p v-if="rows.length === 0" class="stats-empty">
      Für den Saisonvergleich liegen noch keine Ligaspiele mit Spieltagsnummer vor.
    </p>

    <template v-else>
      <div class="stats-scroll">
        <div class="stats-header" role="row">
          <span class="stats-header-cell" role="columnheader">Saison</span>
          <span class="stats-header-cell" role="columnheader">Höchstwert</span>
          <span class="stats-header-cell" role="columnheader">Tiefstwert</span>
          <span class="stats-header-cell" role="columnheader">Ø</span>
        </div>

        <div v-for="row in rows" :key="row.seasonName" class="stats-row" role="row"
             :class="{ 'stats-row-current': row.seasonName === highlightedSeason }">
          <span class="stats-cell stats-season" role="cell" :title="row.seasonName">
            {{ shortSeasonLabel(row.seasonName) }}
          </span>

          <div class="stats-cell stats-match" role="cell">
            <div class="stats-match-meta">
              {{ formatDateShortYear(row.high.date) }} ·
              <span class="stats-day-full">{{ row.high.matchday }}. Spieltag</span>
              <span class="stats-day-short">{{ row.high.matchday }}. Sp.</span>
            </div>
            <div class="stats-match-value">
              <span class="stats-value-badge" :style="recordStyle(row.high.score, row.seasonName === recordHighSeason)"
                    :title="row.seasonName === recordHighSeason ? 'Höchster Einzelwert der gesamten Historie' : null">
                {{ formatScore(row.high.score) }}
              </span>
              <img class="stats-match-logo" :src="row.high.evnt.image" :alt="opponentName(row.high.evnt)"
                   :title="opponentName(row.high.evnt)" loading="lazy" />
              <span class="stats-match-result" :title="resultTitle(row.high.evnt)">
                {{ row.high.evnt.text || '–' }}
              </span>
            </div>
          </div>

          <div class="stats-cell stats-match" role="cell">
            <div class="stats-match-meta">
              {{ formatDateShortYear(row.low.date) }} ·
              <span class="stats-day-full">{{ row.low.matchday }}. Spieltag</span>
              <span class="stats-day-short">{{ row.low.matchday }}. Sp.</span>
            </div>
            <div class="stats-match-value">
              <span class="stats-value-badge" :style="recordStyle(row.low.score, row.seasonName === recordLowSeason)"
                    :title="row.seasonName === recordLowSeason ? 'Tiefster Einzelwert der gesamten Historie' : null">
                {{ formatScore(row.low.score) }}
              </span>
              <img class="stats-match-logo" :src="row.low.evnt.image" :alt="opponentName(row.low.evnt)"
                   :title="opponentName(row.low.evnt)" loading="lazy" />
              <span class="stats-match-result" :title="resultTitle(row.low.evnt)">
                {{ row.low.evnt.text || '–' }}
              </span>
            </div>
          </div>

          <div class="stats-cell stats-avg" role="cell">
            <span class="stats-value-badge" :style="recordStyle(row.avg, row.seasonName === bestAvgSeason)"
                  :title="row.seasonName === bestAvgSeason ? 'Bester Saisondurchschnitt der gesamten Historie' : null">
              {{ formatScore(row.avg) }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  buildSeasonStatsTable, formatDateShortYear, formatScore, moodColor, opponentLabel, resultLabel, shortSeasonLabel
} from './moodHistory.js';

/**
 * Tabellarische Ergaenzung zum Saisonvergleich-Chart: eine Zeile je Saison mit Hoechst-, Tiefst-
 * und Durchschnittswert, jeweils inklusive des zugehoerigen Spiels. Anders als der Chart (der aus
 * Lesbarkeitsgruenden auf MAX_COMPARISON_SEASONS Linien begrenzt ist) zeigt die Tabelle immer die
 * komplette Saison-Historie - siehe buildSeasonStatsTable in moodHistory.js.
 *
 * Bleibt bewusst auf jeder Breite eine einzeilige Tabelle (eine Zeile je Saison) statt ab einem
 * Breakpoint in gestapelte Karten umzuschalten - dafuer werden Abstaende/Schriftgroessen unterhalb
 * von 576px verdichtet statt die Struktur zu wechseln. .stats-scroll faengt nur den Fall ab, in dem
 * selbst das nicht mehr reicht (sehr schmale Geraete, ungewoehnlich lange Saisonnamen).
 *
 * Die Wert-Badges sind standardmaessig neutral (dieselbe Farbe wie stats-match-result) - nur der
 * Tabellen-Rekord je Spalte (die Saison mit dem absoluten Hoechst-, Tiefst- bzw. besten
 * Durchschnittswert der gesamten Historie) bekommt ueber die Ampelfarbe (moodColor) eine Fuellung.
 * So bleibt die Farbe ein knappes Signal fuer genau die Zellen, die den Vergleich tatsaechlich
 * entscheiden, statt an jeder Zahl gleichermassen zu haengen. Eine Skalenlegende braucht es dafuer
 * nicht mehr: bei nur noch drei markierten Zellen liest sich die Farbe (rot = niedrig, gruen =
 * hoch) von selbst.
 */
export default {
  name: 'SeasonStatsTable',
  props: {
    seasons: { type: Array, required: true },
    highlightedSeason: { type: String, default: null }
  },
  computed: {
    table() {
      return buildSeasonStatsTable(this.seasons);
    },
    // Neueste Saison zuerst - dieselbe Reihenfolge wie die Saison-Buttons im Kopf der Karte.
    rows() {
      return [...this.table.rows].reverse();
    },
    recordHighSeason() {
      return this.table.recordHighSeason;
    },
    recordLowSeason() {
      return this.table.recordLowSeason;
    },
    bestAvgSeason() {
      return this.table.bestAvgSeason;
    }
  },
  methods: {
    formatScore,
    formatDateShortYear,
    shortSeasonLabel,
    recordStyle(score, isRecord) {
      return isRecord ? { background: moodColor(score), color: 'var(--chart-surface)' } : null;
    },
    resultTitle(evnt) {
      return resultLabel(evnt);
    },
    opponentName(evnt) {
      return evnt.opp || opponentLabel(evnt) || 'Gegner unbekannt';
    }
  }
};
</script>

<style scoped>
.stats-table {
  background: var(--chart-surface);
  border-radius: var(--radius-md);
  padding: var(--spacing-xs) var(--spacing-sm);
  margin-top: var(--spacing-md);
}

.stats-empty {
  margin: 0;
  padding: var(--spacing-lg);
  text-align: center;
  color: var(--chart-ink-mute);
}

/* Sicherheitsnetz fuer den Fall, dass selbst die verdichteten Groessen unten nicht reichen (sehr
   schmale Geraete, ungewoehnlich lange Saisonnamen): dann scrollt nur diese Zone seitlich, Kopf-
   und Datenzeilen bleiben dabei synchron, weil beide denselben min-width tragen. */
.stats-scroll {
  overflow-x: auto;
}

/* Eine Tabellenzeile je Saison auf jeder Breite - Kopf- und Datenzeilen teilen sich dieselben
   Spurbreiten, damit Ueberschrift und Werte exakt untereinanderstehen. */
.stats-header,
.stats-row {
  display: grid;
  grid-template-columns: 2.3rem minmax(0, 1fr) minmax(0, 1fr) 2.9rem;
  align-items: center;
  gap: 3px 5px;
  min-width: 300px;
}

.stats-header {
  color: var(--chart-ink-mute);
  font-size: 0.68rem;
  padding: 0 4px 3px;
}

.stats-row {
  padding: 5px 4px;
  border-radius: var(--radius-sm);
  border-left: 3px solid transparent;
}

.stats-row:nth-child(even) {
  background: rgba(255, 255, 255, 0.03);
}

.stats-row-current {
  border-left-color: var(--chart-accent);
}

.stats-cell {
  min-width: 0;
}

/* Ohne eigene Meta-Zeile ist die Ø-Zelle niedriger als die Hoechst-/Tiefstwert-Zellen daneben -
   mit dem geerbten align-items: center (siehe .stats-header, .stats-row) haenge sie mittig in der
   Zeile statt auf derselben Linie wie die anderen Wert-Badges zu stehen. */
.stats-avg {
  align-self: end;
}

.stats-season {
  font-weight: 600;
  font-size: 0.8rem;
}

/* Meta-Zeile (Datum, Spieltag) ueber der Wert-Zeile (Badge, Wappen, Ergebnis) statt alles in
   einer flex-wrap-Reihe: so bricht die Zelle auf schmalen Bildschirmen immer an derselben,
   vorhersehbaren Stelle um - nicht mal nach zwei, mal nach drei Zeilen, je nachdem wie viel von
   Datum/Spieltag noch in die erste Zeile gepasst haette. */
.stats-match {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.stats-match-meta {
  color: var(--chart-ink-mute);
  font-size: 0.62rem;
  font-variant-numeric: tabular-nums;
}

/* "Spieltag" vs. "Sp.": unterhalb von 576px ist die Meta-Zeile ohnehin schon knapp bemessen (siehe
   Kommentar an .stats-match oben) - die Abkuerzung schafft dort Luft, ab 576px ist Platz fuer das
   ganze Wort. */
.stats-day-full {
  display: none;
}

.stats-day-short {
  display: inline;
}

.stats-match-value {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Neutral wie stats-match-result - Farbe (siehe recordStyle()) ist ein knappes Signal fuer den
   Tabellen-Rekord, keine durchgaengige Ampel mehr. */
.stats-value-badge {
  display: inline-flex;
  align-items: center;
  min-width: 2.6rem;
  padding: 1px 5px;
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  background: var(--chart-grid);
  font-weight: 700;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
}

.stats-match-logo {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

/* Bewusst ohne Sieg/Remis/Niederlage-Farbe (anders als in MoodMatchList/-Grid): hier zaehlt allein
   der Stimmungswert, das Ergebnis ist nur ergaenzende Information und soll nicht mitvergleichen. */
.stats-match-result {
  flex-shrink: 0;
  color: var(--text-primary);
  background: var(--chart-grid);
  font-weight: 600;
  font-size: 0.66rem;
  border-radius: var(--radius-sm);
  padding: 1px 4px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* Ab 576px ist genug Platz fuer die urspruenglich geraeumigeren Masse - die Struktur (eine Zeile
   je Saison) bleibt exakt dieselbe, nur Abstaende und Schriftgroessen wachsen mit. */
@media (min-width: 576px) {
  .stats-table {
    padding: var(--spacing-sm);
  }

  .stats-header,
  .stats-row {
    grid-template-columns: 6rem minmax(0, 1fr) minmax(0, 1fr) 3.6rem;
    gap: 4px var(--spacing-sm);
  }

  .stats-header {
    font-size: 0.75rem;
    padding: 0 var(--spacing-sm) 4px;
  }

  .stats-row {
    padding: var(--spacing-xs) var(--spacing-sm);
  }

  .stats-season {
    font-size: 1rem;
  }

  .stats-match {
    gap: 2px;
  }

  .stats-match-meta {
    font-size: 0.7rem;
  }

  .stats-day-full {
    display: inline;
  }

  .stats-day-short {
    display: none;
  }

  .stats-match-value {
    gap: 6px;
  }

  .stats-value-badge {
    min-width: 3.4rem;
    padding: 2px 8px;
    font-size: 0.9rem;
  }

  .stats-match-logo {
    width: 20px;
    height: 20px;
  }

  .stats-match-result {
    font-size: 0.75rem;
    padding: 1px 6px;
  }
}
</style>
